/**
 * TanStack Query hooks for the seller catalog-suggestions surface — PIM Wave
 * P3 Task 5. Mirrors `useSellerProducts.ts`'s idiom (fetchers live in the
 * sibling `../api`; this module only wires React Query).
 *
 * `useSellerSuggestions()` backs the `SuggestDialog`'s "N open suggestions"
 * feedback + the 10-open cap pre-check; `useSubmitSuggestion()` posts a new
 * suggestion and invalidates the list so `open_count` refreshes immediately.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  listSellerSuggestions,
  sellerSuggestionKeys,
  submitSuggestion,
  type SellerSuggestionListResponse,
  type SubmitSuggestionBody,
  type SuggestionResponse,
} from "@/features/seller-dashboard/api";

export function useSellerSuggestions(): UseQueryResult<
  SellerSuggestionListResponse,
  Error
> {
  return useQuery<SellerSuggestionListResponse, Error>({
    queryKey: sellerSuggestionKeys.list(),
    queryFn: () => listSellerSuggestions(),
    staleTime: 10_000,
  });
}

export function useSubmitSuggestion(): UseMutationResult<
  SuggestionResponse,
  Error,
  SubmitSuggestionBody
> {
  const qc = useQueryClient();
  return useMutation<SuggestionResponse, Error, SubmitSuggestionBody>({
    mutationFn: (body) => submitSuggestion(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sellerSuggestionKeys.list() });
    },
  });
}
